import type { ApiErrorEnvelope, ApiErrorField } from './api-envelope';
import type { components } from './api-types';

/**
 * HTTP access to the backend.
 *
 * RELATIVE SAME-ORIGIN PATHS ONLY. There is no base URL and no
 * `VITE_*_API_URL` baked at build time — house doctrine in both siblings
 * (CONVENTIONS.md §4.4). In development Vite proxies `/api` to
 * http://localhost:8000; in the cluster nginx proxies `location /api/` to the
 * backend Service. The bundle is byte-identical in every environment, which is
 * what makes "build once, promote the identical tag" true for the frontend.
 */

/** Every request goes under the versioned prefix (CONVENTIONS.md §4.2). */
const API_PREFIX = '/api/v1';

/** Machine code used when the server did not send our envelope at all. */
const UNPARSEABLE_CODE = 'unparseable_error_response';

/**
 * A failed API call, carrying the server's error envelope verbatim.
 *
 * The backend emits exactly one error shape from central handlers:
 *   `{"error": {"code": "...", "message": "..."}, "requestId": "..."}`
 * Surfacing `code` (branch on it) and `requestId` (quote it in a bug report,
 * grep it in the structlog stream) is the entire point of this class — an
 * `Error` with only a message throws that correlation away.
 */
export class ApiError extends Error {
  /** HTTP status. 0 when the request never reached the server. */
  readonly status: number;
  /** `error.code` from the envelope, snake_case, safe to branch on. */
  readonly code: string;
  /** `requestId` from the envelope, else the `x-request-id` response header. */
  readonly requestId: string | null;
  /** The request path, for logging. */
  readonly path: string;
  /**
   * `error.fields` when the server sent one, else empty.
   *
   * Kept on the error rather than re-parsed at the call site because the body
   * is consumed once: a screen that wants to put "must be ≤ 100" next to the
   * input that said 120 has no second chance to read it.
   */
  readonly fields: readonly ApiErrorField[];

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId: string | null;
    path: string;
    fields?: readonly ApiErrorField[];
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.path = init.path;
    this.fields = init.fields ?? [];
  }

  /** True for the two statuses that mean "re-authenticate", not "retry". */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

function isErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}

/** The `fields` array, kept only if every entry is the documented triple. */
function readFields(envelope: ApiErrorEnvelope): readonly ApiErrorField[] {
  const fields = envelope.error.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter(
    (entry): entry is ApiErrorField =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof entry.field === 'string' &&
      typeof entry.message === 'string' &&
      typeof entry.type === 'string'
  );
}

function readRequestId(body: unknown, response: Response): string | null {
  if (typeof body === 'object' && body !== null) {
    const requestId = (body as { requestId?: unknown }).requestId;
    if (typeof requestId === 'string' && requestId.length > 0) return requestId;
  }
  return response.headers.get('x-request-id');
}

/**
 * Resolve a caller path (`/missions`) against the versioned prefix.
 *
 * Anything already under `/api/` is passed through untouched. That covers the
 * versioned product surface and the BFF's `/api/auth/*` endpoints, which sit
 * deliberately outside `/api/v1` because they are a browser redirect flow
 * rather than part of the product contract (CONVENTIONS.md §4.6). Without the
 * pass-through, `/api/auth/logout` would be rewritten to
 * `/api/v1/api/auth/logout` and 404.
 */
function resolve(path: string): string {
  if (path.startsWith('/api/')) return path;
  return `${API_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Not JSON — an nginx 502 page, a gateway timeout, a proxy error.
    return text;
  }
}

async function request<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
  const url = resolve(path);

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
  } catch (cause) {
    // Network failure, DNS, offline, or an aborted request. Give it the same
    // envelope shape so callers only ever handle one error type.
    throw new ApiError({
      status: 0,
      code:
        cause instanceof DOMException && cause.name === 'AbortError'
          ? 'request_aborted'
          : 'network_error',
      message: cause instanceof Error ? cause.message : 'The request could not be sent.',
      requestId: null,
      path: url,
    });
  }

  const body = await readBody(response);

  if (!response.ok) {
    if (isErrorEnvelope(body)) {
      throw new ApiError({
        status: response.status,
        code: body.error.code,
        message: body.error.message,
        requestId: readRequestId(body, response),
        path: url,
        fields: readFields(body),
      });
    }
    throw new ApiError({
      status: response.status,
      code: UNPARSEABLE_CODE,
      message:
        typeof body === 'string' && body.length > 0
          ? body.slice(0, 500)
          : `Request failed with status ${String(response.status)}.`,
      requestId: readRequestId(body, response),
      path: url,
    });
  }

  return body as T;
}

/** GET a JSON resource. Pass `signal` from TanStack Query so it can cancel. */
export function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>('GET', path, init ?? {});
}

/** POST a JSON body and read a JSON response. */
export function postJson<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  return request<T>('POST', path, {
    ...init,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/** PUT a JSON body and read a JSON response. Used by the replace-the-set writes. */
export function putJson<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
  return request<T>('PUT', path, { ...init, body: JSON.stringify(body) });
}

/** PATCH a JSON body and read a JSON response. */
export function patchJson<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
  return request<T>('PATCH', path, { ...init, body: JSON.stringify(body) });
}

/** DELETE a resource. Returns `null` for the usual 204. */
export function deleteJson<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>('DELETE', path, init ?? {});
}

/**
 * Query parameters shared by every fortnight-scoped page endpoint.
 *
 * `fn` is optional on the wire: omitting it resolves the current fortnight —
 * the latest whose meeting date is not in the future — so the SPA never has to
 * compute the calendar itself (CONVENTIONS.md §5.1, decision D2).
 */
export interface FortnightQuery {
  /** Fortnight natural key, e.g. `FN-16`. Omit for the current fortnight. */
  readonly fn?: string | null;
  /** Rows per page. Server default 50, maximum 200. */
  readonly limit?: number;
  /** Rows to skip. */
  readonly offset?: number;
}

/**
 * Append the query string, omitting absent params entirely.
 *
 * Sending `?fn=` or `?fn=null` is not the same request as sending nothing —
 * the first is a validation error, the second is "resolve the current
 * fortnight" — so an unset key must never reach the URL.
 */
function withQuery(path: string, params: FortnightQuery): string {
  const search = new URLSearchParams();
  if (params.fn != null && params.fn !== '') search.set('fn', params.fn);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const query = search.toString();
  return query === '' ? path : `${path}?${query}`;
}

/**
 * `GET /api/v1/missions/{missionId}` — the fortnight scope plus the drill
 * page's grain toggle. `limit`/`offset` are meaningless on a single resource,
 * so they are deliberately absent rather than accepted and ignored.
 */
export interface MissionDetailQuery {
  /** Fortnight natural key, e.g. `FN-16`. Omit for the current fortnight. */
  readonly fn?: string | null;
  /** `fortnight` (stored cadence) or `month` (re-binned at read time). */
  readonly grain?: components['schemas']['MissionDetailRead']['evidenceGrain'];
}

/**
 * The typed facade every feature hook calls. One namespace per resource,
 * mirroring the plural kebab-case route contract in CONVENTIONS.md §4.2.
 *
 * Every response type is read out of `./api-types`, which `make types`
 * regenerates from the live OpenAPI document. Nothing here restates a field
 * name, so a schema change breaks the build rather than the meeting.
 */
export const api = {
  /** The fortnight calendar — the selector, the as-of chip, the publish state. */
  fortnights: {
    /**
     * The whole calendar, newest first. `limit` defaults to the server's 50,
     * which stops short of FN-16 in a 78-fortnight calendar, so callers that
     * need the *current* fortnight ask for the documented maximum.
     */
    list: (
      params: Pick<FortnightQuery, 'limit' | 'offset'> = {},
      init?: RequestInit
    ): Promise<components['schemas']['FortnightPage']> =>
      fetchJson<components['schemas']['FortnightPage']>(withQuery('/fortnights', params), init),

    get: (fnId: string, init?: RequestInit): Promise<components['schemas']['FortnightRead']> =>
      fetchJson<components['schemas']['FortnightRead']>(
        `/fortnights/${encodeURIComponent(fnId)}`,
        init
      ),

    /** Page 1 — the executive pulse, composed server-side in one round trip. */
    pulse: (fnId: string, init?: RequestInit): Promise<components['schemas']['PulseRead']> =>
      fetchJson<components['schemas']['PulseRead']>(
        `/fortnights/${encodeURIComponent(fnId)}/pulse`,
        init
      ),
  },

  /** Page 2 — the seven-mission board, and the tiles page 1 reuses. */
  missions: {
    list: (
      params: FortnightQuery = {},
      init?: RequestInit
    ): Promise<components['schemas']['MissionPage']> =>
      fetchJson<components['schemas']['MissionPage']>(withQuery('/missions', params), init),

    /**
     * Page 6 — one mission's drill: header, evidence trend, deliverables,
     * narrative, open items.
     *
     * `grain` is the storyboard's W/M toggle and it is a SERVER concern: the
     * fortnight is the observation cadence, so a monthly view is a re-bin of
     * stored fortnights, not a second stored series. Sending it as a query
     * param keeps the two views provably derived from the same rows — a
     * browser that re-binned locally could disagree with the server about
     * what July was.
     */
    get: (
      missionId: string,
      params: MissionDetailQuery = {},
      init?: RequestInit
    ): Promise<components['schemas']['MissionDetailRead']> => {
      const { grain, ...fortnight } = params;
      const base = withQuery(`/missions/${encodeURIComponent(missionId)}`, fortnight);
      if (grain === undefined) {
        return fetchJson<components['schemas']['MissionDetailRead']>(base, init);
      }
      const separator = base.includes('?') ? '&' : '?';
      return fetchJson<components['schemas']['MissionDetailRead']>(
        `${base}${separator}grain=${encodeURIComponent(grain)}`,
        init
      );
    },
  },

  /** Page 7 — execution KPI cards, past · today · future. */
  kpis: {
    list: (
      params: FortnightQuery = {},
      init?: RequestInit
    ): Promise<components['schemas']['KpiPage']> =>
      fetchJson<components['schemas']['KpiPage']>(withQuery('/kpis', params), init),
  },

  /** Page 4 — the three asks, the OMD queue, last meeting's follow-through. */
  decisions: {
    get: (
      params: FortnightQuery = {},
      init?: RequestInit
    ): Promise<components['schemas']['DecisionsRead']> =>
      fetchJson<components['schemas']['DecisionsRead']>(withQuery('/decisions', params), init),
  },

  /** Page 5 — blocker cards, the dependency tracker and the risk pulse. */
  blockers: {
    get: (
      params: FortnightQuery = {},
      init?: RequestInit
    ): Promise<components['schemas']['BlockersRead']> =>
      fetchJson<components['schemas']['BlockersRead']>(withQuery('/blockers', params), init),
  },

  /** Page 3 — phases, the versioned baseline, gates, slippage, next 14 days. */
  plan: {
    get: (
      params: FortnightQuery = {},
      init?: RequestInit
    ): Promise<components['schemas']['PlanRead']> =>
      fetchJson<components['schemas']['PlanRead']>(withQuery('/plan', params), init),
  },

  /**
   * Page 3's readiness panel, standalone. `gate` is required: each gate has
   * its OWN evidence conditions and its own thresholds (G1 500/65, G2
   * 2,000/75, G3 85), so a caller must say which gate it is asking about.
   */
  gateEvidence: {
    get: (
      params: FortnightQuery & { readonly gate: string },
      init?: RequestInit
    ): Promise<components['schemas']['GateEvidenceRead']> => {
      const { gate, ...fortnight } = params;
      const base = withQuery('/gate-evidence', fortnight);
      const separator = base.includes('?') ? '&' : '?';
      return fetchJson<components['schemas']['GateEvidenceRead']>(
        `${base}${separator}gate=${encodeURIComponent(gate)}`,
        init
      );
    },
  },

  /** The annex-owned `ref_*` registers, by set name. Not fortnight-scoped. */
  reference: {
    get: (
      set: components['schemas']['ReferenceRead']['set'],
      init?: RequestInit
    ): Promise<components['schemas']['ReferenceRead']> =>
      fetchJson<components['schemas']['ReferenceRead']>(`/reference/${set}`, init),
  },

  meta: {
    /**
     * The OpenAPI document the TypeScript client is generated from. Useful as
     * a liveness probe from the SPA because it is the one `/api/v1` path
     * guaranteed to exist in every build.
     */
    openapi: (init?: RequestInit): Promise<unknown> => fetchJson<unknown>('/openapi.json', init),
  },
} as const;
